/**
 * Dashboard Date Reference Dropdown
 * Controlled dropdown for date reference selection (no React Hook Form)
 */

import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback } from 'react';
import type { InvoiceSearchRequestDto } from 'src/generated/services/TFinancialApi';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '180px',
  [theme.breakpoints.down(1520)]: {
    width: '140px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
}));

interface DateReferenceOption {
  label: string;
  value: InvoiceSearchRequestDto['dateReference'];
}

// Date reference options
export const dateReferenceOptions: DateReferenceOption[] = [
  {
    label: 'Pickup Date',
    value: 'PICKUP_DATE',
  },
  {
    label: 'Delivery Date',
    value: 'DELIVERY_DATE',
  },
  {
    label: 'Invoice Date',
    value: 'INVOICE_DATE',
  },
  {
    label: 'Invoice Due Date',
    value: 'INVOICE_DUE_DATE',
  },
  {
    label: 'Submission Date',
    value: 'SUBMISSION_DATE',
  },
];

interface DashboardDateDropdownProps {
  value: InvoiceSearchRequestDto['dateReference'];
  onChange: (value: InvoiceSearchRequestDto['dateReference']) => void;
  disabled?: boolean;
  id?: string;
}

export const DashboardDateDropdown = ({ value, onChange, disabled, id }: DashboardDateDropdownProps) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onChange(event.target.value as InvoiceSearchRequestDto['dateReference']);
    },
    [onChange]
  );

  const renderValue = useCallback((selected: string) => {
    const option = dateReferenceOptions.find((opt) => opt.value === selected);
    return <span className='text-14 xl:text-16'>{option?.label || selected}</span>;
  }, []);

  return (
    <StyledFormControl size='small' id={id}>
      <InputLabel>Date Reference</InputLabel>
      <Select
        value={value || 'PICKUP_DATE'}
        onChange={handleChange}
        input={<OutlinedInput label='Date Reference' />}
        renderValue={renderValue}
        disabled={disabled}
        MenuProps={{
          sx: {
            '& .MuiPaper-root': {
              boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
              border: '1px solid #DFDFDF',
            },
          },
        }}
      >
        {dateReferenceOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <span className='text-14 xl:text-16'>{option.label}</span>
          </MenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};
